/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';

// Style, SEO and settings
import Helmet from 'react-helmet';
import { Col, css, Grid, Row, up, styled, withTheme } from '@smooth-ui/core-sc';

// Additional Components/Containers
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';

import { withSettings } from 'containers/WebApp/SettingsContext';
import bgImage1x from './images/LoginPage@1x.png';
import bgImage2x from './images/LoginPage@2x.png';
//import overlay from './images/soverlay@2x.png';
import logo from '../../theme/rsr-logo.svg';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.section`
  align-items: center;
  display: flex;
  flex: 1 0 auto;
  flex-direction: row;
  justify-content: center;

  background-image: url(${bgImage1x});
  background-position: right top;
  background-repeat: no-repeat;
  background-size: cover;

  @media only screen and (min-device-pixel-ratio: 1.5),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-image: url(${bgImage2x});
  }

  ${up(
    'xs',
    css`
      border-top: 2.5rem solid #cd1723;
    `
  )};

  ${up(
    'lg',
    css`
      border-top: none;

      &:before {
        content: '';
        position: absolute;
        inset: 0;
        width: 35vw;
        background: rgba(205, 23, 35, 1);
        transform: skew(-15deg);
        transform-origin: top;
        margin-bottom: 36px;
      }
    `
  )};

  ${up(
    'lg',
    css`
      flex-direction: column;
      justify-content: center;
    `
  )};
`;

const LoginLogo = styled.h1`
  margin-bottom: 0;
  margin-top: 0;
  padding-bottom: 1rem;
  padding-top: 0;
  img {
    height: auto;
    width: 323px;
  }
`;

const IntroContainer = styled.div`
  box-sizing: border-box;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  width: 100%;

  h2 {
    font-size: 30px;
  }

  a {
    color: #707070;
    &:hover,
    &:active {
      color: #cd1723;
    }
    &:visited {
      color: #707070;
    }
  }
`;

const LoginContainer = styled.div`
  background-color: rgb(240, 240, 240, 0.85);
  color: #707070;
  font-size: 15px;
  padding: 2rem;
  max-width: 450px;

  .reset-password {
    a {
      font-size: 14px;
      font-weight: normal;
      &:hover,
      &:active {
        color: #cd1723;
      }
      &:visited {
        color: #707070;
      }
    }
  }

  .login-form {
    .sui-label {
      display: none;
    }

    .sui-button {
      text-transform: uppercase;
      font-weight: bold;
      font-size: 18px;
      padding: 0.5rem 1rem;
    }
  }
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <PageContainer className="page-container">
      <IntroContainer>
        <Grid>
          <Row justifyContent="center">
            <Col xs={12}>
              <LoginContainer>
                <LoginLogo className="text--center">
                  <img
                    src={logo}
                    alt={
                      settings.settingsApp && settings.settingsApp.siteName
                        ? settings.settingsApp.siteName
                        : 'ProgramName'
                    }
                  />
                </LoginLogo>
                <p>
                  <a
                    href="https://res.cloudinary.com/incremental/image/upload/v1743382884/RSRRewards/site/How-do-I-get-RSR.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    How do I get an RSR account?
                  </a>
                  <br />
                  <a
                    href="https://res.cloudinary.com/incremental/image/upload/v1743382884/RSRRewards/site/What-is-RSR-Rewards.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    What is RSR?
                  </a>
                </p>
                <LoginProvider disableMeta>
                  <LoginForm
                    formLabels={{ labelUserName: 'Username (Account Code)' }}
                  />
                </LoginProvider>
                <p>
                  If you are having difficulties logging in,
                  <br />
                  please contact us on <a href="tel:0508777775">0508 777 775</a>
                  .
                </p>
              </LoginContainer>
            </Col>
          </Row>
        </Grid>
      </IntroContainer>
    </PageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
